import { useCallback, useEffect, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../common/state/hooks";
import { PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID } from "../../projectGroups/components/ProjectGroupList";
import {
  fetchSearchIndex,
  getSearchIndex,
  searchProjectGroups,
} from "../../projectGroups/state/projectGroupsSlice";
import { enqueueOnboardingEvent } from "../events/eventQueue";
import type { OnboardingEventMeta } from "../events/eventQueue";
import { getTour, getToursForAudience, isAwaitStep } from "../registry";
import type { AnchorTourStep, TourDefinition } from "../registry/types";
import type { OnboardingOp } from "../state/onboardingTypes";
import {
  applyOnboardingOps,
  endTour,
  findRejoinableTours,
  findSuspendedParent,
  goToStep,
  launchTour,
  markTourRouteReached,
  markStepSkipped,
  markStepViewed,
  markTourStartAnnounced,
  pinTourPathname,
  reachAwaitRoute,
  selectActiveTour,
  selectDismissWatch,
  selectOnboardingAudience,
  selectOnboardingRemote,
  selectSuggestionsMuted,
  selectPendingTour,
  setDismissWatch,
  setPendingTour,
  setLocationHintTour,
  startTour,
} from "../state/onboardingSlice";
import { getAnchorRevealer, getStepPreparer } from "./stepPreparers";
import {
  queryVisibleAnchor,
  waitForAnchorSettled,
  waitForFirstAnchorSettled,
} from "./waitForAnchor";
import type { AnchorWaitResult } from "./waitForAnchor";

const HINT_FOLLOW_TIMEOUT_MS = 10000;
const STEP_ANCHOR_TIMEOUT_MS = 4000;
const AFTER_AWAIT_ANCHOR_TIMEOUT_MS = 15000;
const REMOUNTED_ANCHOR_TIMEOUT_MS = 1500;
const PENDING_TOUR_TIMEOUT_MS = 60000;
const PROGRESS_DEBOUNCE_MS = 500;

export type TourPauseReason =
  | "route_change"
  | "target_click"
  | "target_removed"
  | "dismissed"
  | "scope_left";

const isHintAnchorStep = (step: TourDefinition["steps"][number] | undefined): boolean =>
  Boolean(step && !isAwaitStep(step) && step.hint);

const collectHintBlock = (
  tour: TourDefinition,
  stepIndex: number,
): { anchor: string; index: number }[] => {
  const block: { anchor: string; index: number }[] = [];
  for (let index = stepIndex - 1; index >= 0 && isHintAnchorStep(tour.steps[index]); index -= 1) {
    const candidate = tour.steps[index] as AnchorTourStep;
    block.push({ anchor: candidate.anchor, index });
  }
  for (
    let index = stepIndex + 1;
    index < tour.steps.length && isHintAnchorStep(tour.steps[index]);
    index += 1
  ) {
    const candidate = tour.steps[index] as AnchorTourStep;
    block.push({ anchor: candidate.anchor, index });
  }
  return block;
};

const followsSignal = (tour: TourDefinition, stepIndex: number): boolean => {
  for (let index = stepIndex - 1; index >= 0; index -= 1) {
    const step = tour.steps[index];
    if (!step || isAwaitStep(step)) {
      return false;
    }
    if (step.advanceOn === "signal") {
      return true;
    }
    if (step.hint || step.advanceOn === "targetClick") {
      return false;
    }
  }
  return false;
};

const firstVisibleStepIndex = (steps: TourDefinition["steps"]): number =>
  Math.max(
    0,
    steps.findIndex((candidate) => !isAwaitStep(candidate) && queryVisibleAnchor(candidate.anchor)),
  );

const observeAnchors = (check: () => void): (() => void) => {
  let frame: number | null = null;
  const observer = new MutationObserver(() => {
    if (frame === null) {
      frame = requestAnimationFrame(() => {
        frame = null;
        check();
      });
    }
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["data-tour", "style", "class", "hidden"],
  });
  check();
  return () => {
    observer.disconnect();
    if (frame !== null) {
      cancelAnimationFrame(frame);
    }
  };
};

let progressTimer: ReturnType<typeof setTimeout> | null = null;
let targetClickInFlight = false;

const cancelScheduledProgress = (): void => {
  if (progressTimer) {
    clearTimeout(progressTimer);
    progressTimer = null;
  }
};

const eventMeta = (
  meta: OnboardingEventMeta | undefined,
  replay: boolean,
): { meta: OnboardingEventMeta } | Record<string, never> => {
  if (replay) {
    return { meta: { ...(meta ?? {}), replay: true } };
  }
  return meta ? { meta } : {};
};

const scheduleProgress = (write: () => void): void => {
  cancelScheduledProgress();
  progressTimer = setTimeout(() => {
    progressTimer = null;
    write();
  }, PROGRESS_DEBOUNCE_MS);
};

const usePrefersReducedMotion = (): boolean => {
  const [value, setValue] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setValue(mql.matches);
    mql.addEventListener("change", onChange);
    return () => {
      mql.removeEventListener("change", onChange);
    };
  }, []);

  return value;
};

export type UseTourRunnerResult = {
  tour: TourDefinition | null;
  step: AnchorTourStep | null;
  stepIndex: number;
  targetEl: HTMLElement | null;
  prefersReducedMotion: boolean;
  next: () => void;
  back: () => void;
  skip: () => void;
  dismiss: () => void;
  handoff: () => void;
};

export const useTourRunner = (): UseTourRunnerResult => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const activeTour = useAppSelector(selectActiveTour);
  const audience = useAppSelector(selectOnboardingAudience);
  const pendingTour = useAppSelector(selectPendingTour);
  const dismissWatch = useAppSelector(selectDismissWatch);
  const remote = useAppSelector(selectOnboardingRemote);
  const suggestionsMuted = useAppSelector(selectSuggestionsMuted);
  const searchIndex = useAppSelector(getSearchIndex);
  const prefersReducedMotion = usePrefersReducedMotion();

  const tour = activeTour ? getTour(activeTour.id, audience) ?? null : null;
  const stepIndex = activeTour?.stepIndex ?? 0;
  const currentStep = tour?.steps[stepIndex] ?? null;
  const step = currentStep && !isAwaitStep(currentStep) ? currentStep : null;
  const awaitStep = currentStep && isAwaitStep(currentStep) ? currentStep : null;

  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);

  const activeTourRef = useRef(activeTour);
  activeTourRef.current = activeTour;
  const remoteRef = useRef(remote);
  remoteRef.current = remote;
  const activeTourId = activeTour?.id ?? null;
  const activeStepIndex = activeTour?.stepIndex ?? null;
  const activeSource = activeTour?.source ?? null;
  const activeReplay = activeTour?.replay ?? false;
  const pinnedPathname = activeTour?.pathname ?? null;
  const branchParentId = activeTour?.branchParentId ?? null;
  const routeGate = activeTour?.routeGate ?? null;
  const awaitRouteIndex = activeTour?.awaitRouteIndex ?? -1;
  const onTourRoute = !routeGate || Boolean(matchPath(routeGate, location.pathname));

  const pendingTourRef = useRef(pendingTour);
  pendingTourRef.current = pendingTour;
  const routeReached = activeTour?.routeReached ?? false;

  const writeProgress = useCallback(
    (op: OnboardingOp, replay = false) => {
      if (op.op === "tourProgress" && getTour(op.id, audience)?.hidden) {
        return;
      }
      void dispatch(
        applyOnboardingOps([op.op === "tourProgress" && replay ? { ...op, replay: true } : op]),
      );
    },
    [dispatch, audience],
  );

  const finishTour = useCallback(
    (status: "completed" | "skipped", atStepOverride?: number) => {
      const current = activeTourRef.current;
      if (!current) {
        return;
      }
      activeTourRef.current = null;
      cancelScheduledProgress();
      writeProgress(
        {
          op: "tourProgress",
          id: current.id,
          status,
          step: atStepOverride ?? current.stepIndex,
        },
        current.replay,
      );
      if (status === "completed" && current.branchParentId) {
        const parentEntry = remoteRef.current?.tours[current.branchParentId];
        if (parentEntry?.status === "suspended") {
          writeProgress(
            {
              op: "tourProgress",
              id: current.branchParentId,
              status: "completed",
              step: parentEntry.step,
            },
            current.replay,
          );
        }
      }
      dispatch(endTour());
    },
    [dispatch, writeProgress],
  );

  const completeTour = useCallback(
    (atStepOverride?: number) => {
      const current = activeTourRef.current;
      if (!current) {
        return;
      }
      enqueueOnboardingEvent({
        event: "tour_completed",
        surface: current.source,
        itemId: current.id,
        step: atStepOverride ?? current.stepIndex,
        ...eventMeta(undefined, current.replay),
      });
      finishTour("completed", atStepOverride);
    },
    [finishTour],
  );

  const skipTour = useCallback(
    (reason: "explicit" | "target_missing", atStepOverride?: number) => {
      const current = activeTourRef.current;
      if (!current) {
        return;
      }
      enqueueOnboardingEvent({
        event: "tour_skipped",
        surface: current.source,
        itemId: current.id,
        step: atStepOverride ?? current.stepIndex,
        ...eventMeta({ reason }, current.replay),
      });
      finishTour("skipped", atStepOverride);
      if (getTour(current.id, audience)?.hidden) {
        dispatch(setPendingTour(null));
      }
    },
    [finishTour, dispatch, audience],
  );

  const pauseTour = useCallback(
    (reason: TourPauseReason, atStep?: number) => {
      const current = activeTourRef.current;
      if (!current) {
        return;
      }
      activeTourRef.current = null;
      cancelScheduledProgress();
      const shownStep = getTour(current.id, audience)?.steps[current.stepIndex];
      const isHintStep = Boolean(shownStep && !isAwaitStep(shownStep) && shownStep.hint);
      const ownPoint =
        current.pathname !== null
          ? { stepIndex: current.stepIndex, pathname: current.pathname }
          : null;
      const point =
        (!isHintStep ? ownPoint : current.resumePoint) ??
        (reason === "dismissed" ? ownPoint : null);
      const pausedStep = atStep ?? point?.stepIndex ?? current.stepIndex;
      writeProgress(
        {
          op: "tourProgress",
          id: current.id,
          status: "paused",
          step: pausedStep,
          ...(point ? { route: point.pathname } : {}),
        },
        current.replay,
      );
      enqueueOnboardingEvent({
        event: "tour_paused",
        surface: current.source,
        itemId: current.id,
        step: pausedStep,
        ...eventMeta({ reason }, current.replay),
      });
      dispatch(endTour());
    },
    [dispatch, writeProgress, audience],
  );

  const next = useCallback(() => {
    const current = activeTourRef.current;
    if (!current) {
      return;
    }
    const currentTour = getTour(current.id, audience);
    if (!currentTour) {
      return;
    }
    if (current.stepIndex >= currentTour.steps.length - 1) {
      completeTour();
    } else {
      dispatch(goToStep(current.stepIndex + 1));
    }
  }, [dispatch, completeTour, audience]);

  const back = useCallback(() => {
    const current = activeTourRef.current;
    if (!current || current.stepIndex === 0) {
      return;
    }
    const previousStep = getTour(current.id, audience)?.steps[current.stepIndex - 1];
    if (!previousStep || isAwaitStep(previousStep)) {
      return;
    }
    dispatch(goToStep(current.stepIndex - 1));
  }, [dispatch, audience]);

  const skip = useCallback(() => {
    skipTour("explicit");
  }, [skipTour]);

  const dismiss = useCallback(() => {
    const current = activeTourRef.current;
    if (!current) {
      return;
    }
    const currentTour = getTour(current.id, audience);
    if (!currentTour || currentTour.hidden) {
      skipTour("explicit");
      return;
    }
    if (!current.autoReshown) {
      const visibleAtDismiss = currentTour.steps
        .slice(current.stepIndex + 1)
        .flatMap((laterStep) =>
          !isAwaitStep(laterStep) && queryVisibleAnchor(laterStep.anchor) ? [laterStep.anchor] : [],
        );
      dispatch(
        setDismissWatch({
          id: current.id,
          source: current.source,
          fromStep: current.stepIndex,
          visibleAtDismiss,
        }),
      );
    }
    pauseTour("dismissed");
  }, [audience, dispatch, pauseTour, skipTour]);

  const handoff = useCallback(() => {
    const current = activeTourRef.current;
    if (!current) {
      return;
    }
    const shownStep = getTour(current.id, audience)?.steps[current.stepIndex];
    const handoffTourId =
      shownStep && !isAwaitStep(shownStep) ? shownStep.handoffTourId : undefined;
    completeTour();
    if (handoffTourId) {
      void dispatch(launchTour({ id: handoffTourId, source: "tour" }));
    }
  }, [dispatch, completeTour, audience]);

  useEffect(() => {
    if (!activeTour || activeTour.startAnnounced) {
      return;
    }
    if (!getTour(activeTour.id, audience)) {
      return;
    }
    if (activeTour.routeGate && !matchPath(activeTour.routeGate, location.pathname)) {
      navigate(activeTour.routeGate);
    }
    dispatch(markTourStartAnnounced());
    if (activeTour.resumed) {
      enqueueOnboardingEvent({
        event: "tour_resumed",
        surface: activeTour.source,
        itemId: activeTour.id,
        step: activeTour.stepIndex,
        ...eventMeta(
          activeTour.autoReshown
            ? { via: "auto_reshow" }
            : activeTour.rejoined
            ? { via: "rejoin" }
            : undefined,
          activeTour.replay,
        ),
      });
      writeProgress(
        {
          op: "tourProgress",
          id: activeTour.id,
          status: "started",
          step: activeTour.stepIndex,
          ...(activeTour.routeGate ? { route: activeTour.routeGate } : {}),
        },
        activeTour.replay,
      );
      return;
    }
    enqueueOnboardingEvent({
      event: "tour_started",
      surface: activeTour.source,
      itemId: activeTour.id,
      step: activeTour.stepIndex,
      ...eventMeta(activeTour.inPlace ? { via: "in_place" } : undefined, activeTour.replay),
    });
    writeProgress(
      {
        op: "tourProgress",
        id: activeTour.id,
        status: "started",
        step: activeTour.stepIndex,
      },
      activeTour.replay,
    );
  }, [activeTour, dispatch, navigate, location.pathname, writeProgress, audience]);

  useEffect(() => {
    if (!activeTourId) {
      return;
    }
    if (onTourRoute) {
      if (!routeReached) {
        dispatch(markTourRouteReached());
      }
      return;
    }
    if (routeReached && pinnedPathname === null && !targetClickInFlight) {
      pauseTour("route_change");
    }
  }, [
    activeTourId,
    onTourRoute,
    routeReached,
    pinnedPathname,
    location.pathname,
    pauseTour,
    dispatch,
  ]);

  useEffect(() => {
    if (!activeTourId || !pinnedPathname) {
      return;
    }
    if (pinnedPathname === window.location.pathname || targetClickInFlight) {
      return;
    }
    if (step?.wizardRoutes?.some((route) => matchPath(route, window.location.pathname))) {
      dispatch(pinTourPathname(null));
      return;
    }
    const upcomingAwait = tour && stepIndex !== null ? tour.steps[stepIndex + 1] : undefined;
    if (
      step?.hint &&
      upcomingAwait &&
      isAwaitStep(upcomingAwait) &&
      upcomingAwait.allowedRoutes.some((route) => matchPath(route, window.location.pathname))
    ) {
      dispatch(pinTourPathname(null));
      dispatch(goToStep(stepIndex + 1));
      return;
    }
    pauseTour("route_change");
  }, [activeTourId, pinnedPathname, location.pathname, pauseTour, step, stepIndex, tour, dispatch]);

  useEffect(() => {
    if (!activeTourId || !awaitStep) {
      return;
    }
    const routeIndex = awaitStep.allowedRoutes.findIndex((route) =>
      Boolean(matchPath(route, window.location.pathname)),
    );
    if (routeIndex === -1 || routeIndex < awaitRouteIndex) {
      pauseTour("route_change");
      return;
    }
    if (routeIndex > awaitRouteIndex) {
      dispatch(reachAwaitRoute(routeIndex));
    }
  }, [activeTourId, awaitStep, awaitRouteIndex, location.pathname, pauseTour, dispatch]);

  useEffect(() => {
    if (
      !activeTourId ||
      activeStepIndex === null ||
      !activeSource ||
      !tour ||
      !step ||
      !onTourRoute
    ) {
      setTargetEl(null);
      return undefined;
    }

    const controller = new AbortController();
    const stepId = activeTourId;
    const capturedStepIndex = activeStepIndex;
    const surface = activeSource;
    const capturedReplay = activeReplay;

    const isStale = (): boolean => {
      const current = activeTourRef.current;
      return !current || current.id !== stepId || current.stepIndex !== capturedStepIndex;
    };

    setTargetEl(null);

    let detachObserver: MutationObserver | null = null;

    const previousStep = capturedStepIndex > 0 ? tour.steps[capturedStepIndex - 1] : undefined;
    const anchorTimeoutMs =
      (previousStep && isAwaitStep(previousStep)) ||
      followsSignal(tour, capturedStepIndex) ||
      (capturedStepIndex === 0 && tour.hidden)
        ? AFTER_AWAIT_ANCHOR_TIMEOUT_MS
        : STEP_ANCHOR_TIMEOUT_MS;

    const followWizard = (): void => {
      const nextStep = tour.steps[capturedStepIndex + 1];
      const previousFollowStep = tour.steps[capturedStepIndex - 1];
      const candidates = [step.anchor];
      const nextAnchor = nextStep && !isAwaitStep(nextStep) ? nextStep.anchor : null;
      const laterHints: { anchor: string; index: number }[] = [];
      for (
        let index = capturedStepIndex + 1;
        index < tour.steps.length && isHintAnchorStep(tour.steps[index]);
        index += 1
      ) {
        if (index > capturedStepIndex + 1) {
          laterHints.push({ anchor: (tour.steps[index] as AnchorTourStep).anchor, index });
        }
      }
      const previousAnchor =
        previousFollowStep && !isAwaitStep(previousFollowStep) && previousFollowStep.hint
          ? previousFollowStep.anchor
          : null;
      if (nextAnchor) {
        candidates.push(nextAnchor);
      }
      if (previousAnchor) {
        candidates.push(previousAnchor);
      }
      candidates.push(...laterHints.map(({ anchor }) => anchor));
      void waitForFirstAnchorSettled(candidates, HINT_FOLLOW_TIMEOUT_MS, controller.signal).then(
        ({ found }) => {
          if (controller.signal.aborted || isStale()) {
            return;
          }
          if (found?.anchorId === step.anchor) {
            setTargetEl(found.el);
            watchDetach(found.el);
            return;
          }
          if (found && found.anchorId === nextAnchor) {
            dispatch(goToStep(capturedStepIndex + 1));
            return;
          }
          if (found && found.anchorId === previousAnchor) {
            dispatch(goToStep(capturedStepIndex - 1));
            return;
          }
          const laterHint = found
            ? laterHints.find(({ anchor }) => anchor === found.anchorId)
            : undefined;
          if (laterHint) {
            dispatch(goToStep(laterHint.index));
            return;
          }
          if (nextStep && isAwaitStep(nextStep)) {
            dispatch(pinTourPathname(null));
            dispatch(goToStep(capturedStepIndex + 1));
            return;
          }
          pauseTour("target_removed");
        },
      );
    };

    const watchDetach = (el: HTMLElement): void => {
      detachObserver?.disconnect();
      detachObserver = new MutationObserver(() => {
        if (isStale() || (el.isConnected && el.getAttribute("data-tour") === step.anchor)) {
          return;
        }
        detachObserver?.disconnect();
        setTargetEl(null);
        if (step.hint) {
          followWizard();
          return;
        }
        void waitForAnchorSettled(step.anchor, REMOUNTED_ANCHOR_TIMEOUT_MS, controller.signal).then(
          ({ found: remounted }) => {
            if (controller.signal.aborted || isStale()) {
              return;
            }
            if (!remounted) {
              if (targetClickInFlight) {
                return;
              }
              if (!step.branchOn) {
                pauseTour("target_removed");
                return;
              }
              void waitForAnchorSettled(
                step.branchOn.anchor,
                REMOUNTED_ANCHOR_TIMEOUT_MS,
                controller.signal,
              ).then(({ found: branchTarget }) => {
                if (controller.signal.aborted || isStale()) {
                  return;
                }
                if (!branchTarget) {
                  pauseTour("target_removed");
                }
              });
              return;
            }
            setTargetEl(remounted);
            watchDetach(remounted);
          },
        );
      });
      detachObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["data-tour"],
      });
    };

    const showTarget = (el: HTMLElement, scroll: boolean): void => {
      const route = window.location.pathname;
      dispatch(pinTourPathname(route));
      setTargetEl(el);
      if (scroll && !step.hint) {
        el.scrollIntoView({
          block: "center",
          inline: "nearest",
          behavior: prefersReducedMotion ? "auto" : "smooth",
        });
      } else if (scroll) {
        el.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: prefersReducedMotion ? "auto" : "smooth",
        });
      }
      enqueueOnboardingEvent({
        event: "tour_step_viewed",
        surface,
        itemId: tour.id,
        step: capturedStepIndex,
      });
      dispatch(markStepViewed(capturedStepIndex));
      scheduleProgress(() => {
        writeProgress(
          {
            op: "tourProgress",
            id: tour.id,
            status: "started",
            step: capturedStepIndex,
            route,
          },
          capturedReplay,
        );
      });

      watchDetach(el);
    };

    const hintBlock = step.hint ? collectHintBlock(tour, capturedStepIndex) : [];
    const revealer = getAnchorRevealer(step.anchor);
    const reveal: Promise<void> =
      revealer && !queryVisibleAnchor(step.anchor)
        ? Promise.resolve(revealer()).catch(() => undefined)
        : Promise.resolve();
    const resolveTarget: Promise<AnchorWaitResult<{ anchorId: string; el: HTMLElement }>> =
      reveal.then(() =>
        step.hint
          ? waitForFirstAnchorSettled(
              [step.anchor, ...hintBlock.map(({ anchor }) => anchor)],
              anchorTimeoutMs,
              controller.signal,
            )
          : waitForAnchorSettled(step.anchor, anchorTimeoutMs, controller.signal).then(
              ({ found, reason }) => ({
                found: found ? { anchorId: step.anchor, el: found } : null,
                reason,
              }),
            ),
      );

    void resolveTarget.then(({ found, reason }) => {
      if (controller.signal.aborted || isStale()) {
        return;
      }

      if (found && found.anchorId !== step.anchor) {
        const sibling = hintBlock.find(({ anchor }) => anchor === found.anchorId);
        if (sibling) {
          for (let index = capturedStepIndex; index < sibling.index; index += 1) {
            dispatch(markStepSkipped(index));
          }
          dispatch(goToStep(sibling.index));
          return;
        }
      }

      const el = found?.el ?? null;
      if (el) {
        const preparer = getStepPreparer(step.anchor);
        if (!preparer) {
          showTarget(el, true);
          return;
        }
        void preparer(el)
          .catch(() => undefined)
          .then(() => {
            if (!controller.signal.aborted && !isStale()) {
              showTarget(el, false);
            }
          });
        return;
      }

      setTargetEl(null);
      enqueueOnboardingEvent({
        event: "tour_target_missing",
        surface,
        itemId: tour.id,
        step: capturedStepIndex,
        meta: { reason },
      });

      let nextIndex = capturedStepIndex + 1;
      if (step.advanceOn === "targetClick") {
        let skippedAwait = false;
        for (
          let candidate = tour.steps[nextIndex];
          candidate && (isAwaitStep(candidate) || candidate.hint);
          candidate = tour.steps[nextIndex]
        ) {
          skippedAwait = skippedAwait || isAwaitStep(candidate);
          nextIndex += 1;
        }
        if (skippedAwait && nextIndex < tour.steps.length - 1) {
          nextIndex += 1;
        }
      }
      while (nextIndex < tour.steps.length && isAwaitStep(tour.steps[nextIndex])) {
        nextIndex += 1;
      }
      if (nextIndex < tour.steps.length) {
        dispatch(markStepSkipped(capturedStepIndex));
        dispatch(goToStep(nextIndex));
      } else if ((activeTourRef.current?.viewedSteps.length ?? 0) > 0) {
        completeTour(capturedStepIndex);
      } else if (activeTourRef.current?.resumed) {
        pauseTour("target_removed");
      } else {
        skipTour("target_missing", capturedStepIndex);
      }
    });

    return () => {
      controller.abort();
      detachObserver?.disconnect();
    };
  }, [
    activeTourId,
    activeStepIndex,
    activeSource,
    tour,
    step,
    onTourRoute,
    dispatch,
    prefersReducedMotion,
    writeProgress,
    pauseTour,
    completeTour,
    skipTour,
  ]);

  const branchOn = step?.branchOn ?? null;

  useEffect(() => {
    if (!activeTourId || !branchOn || branchParentId !== null || !onTourRoute) {
      return undefined;
    }
    return observeAnchors(() => {
      const current = activeTourRef.current;
      if (!current || current.id !== activeTourId || !queryVisibleAnchor(branchOn.anchor)) {
        return;
      }
      activeTourRef.current = null;
      cancelScheduledProgress();
      writeProgress(
        {
          op: "tourProgress",
          id: current.id,
          status: "suspended",
          step: current.stepIndex,
        },
        current.replay,
      );
      enqueueOnboardingEvent({
        event: "tour_paused",
        surface: current.source,
        itemId: current.id,
        step: current.stepIndex,
        meta: { reason: "branch" },
      });
      dispatch(endTour());
      const childStepIndex = firstVisibleStepIndex(getTour(branchOn.tourId, audience)?.steps ?? []);
      dispatch(
        startTour({
          id: branchOn.tourId,
          source: "tour",
          stepIndex: childStepIndex,
          branchParentId: current.id,
          replay: current.replay,
        }),
      );
      for (let index = 0; index < childStepIndex; index += 1) {
        dispatch(markStepSkipped(index));
      }
    });
  }, [activeTourId, branchOn, branchParentId, onTourRoute, audience, dispatch, writeProgress]);

  useEffect(() => {
    if (activeTour || pendingTour || suggestionsMuted || !remote || !audience) {
      return undefined;
    }
    const autoStartable = getToursForAudience(audience).filter(
      (candidate) => candidate.autoStartAnchor && !remote.tours[candidate.id],
    );
    if (autoStartable.length === 0) {
      return undefined;
    }
    return observeAnchors(() => {
      if (activeTourRef.current) {
        return;
      }
      const found = autoStartable.find(
        (candidate) => candidate.autoStartAnchor && queryVisibleAnchor(candidate.autoStartAnchor),
      );
      if (!found) {
        return;
      }
      const stepIndex = firstVisibleStepIndex(found.steps);
      dispatch(
        startTour({
          id: found.id,
          source: "tour",
          stepIndex,
          route: window.location.pathname,
        }),
      );
      for (let index = 0; index < stepIndex; index += 1) {
        dispatch(markStepSkipped(index));
      }
    });
  }, [activeTour, pendingTour, remote, audience, suggestionsMuted, dispatch]);

  const parentEntry = branchParentId ? remote?.tours[branchParentId] : undefined;
  const parentBranchStep =
    branchParentId && parentEntry?.status === "suspended"
      ? getTour(branchParentId, audience)?.steps[parentEntry.step]
      : undefined;
  const branchAnchor =
    parentBranchStep && !isAwaitStep(parentBranchStep)
      ? parentBranchStep.branchOn?.anchor ?? null
      : null;
  const childAwaitIndex = tour ? tour.steps.findIndex((candidate) => isAwaitStep(candidate)) : -1;
  const beforeChildAwait = childAwaitIndex === -1 || stepIndex < childAwaitIndex;

  useEffect(() => {
    if (!activeTourId || !branchParentId || !branchAnchor || !parentEntry || !beforeChildAwait) {
      return undefined;
    }
    const parentStep = parentEntry.step;
    return observeAnchors(() => {
      const current = activeTourRef.current;
      if (
        !current ||
        current.id !== activeTourId ||
        current.pathname === null ||
        current.pathname !== window.location.pathname ||
        queryVisibleAnchor(branchAnchor)
      ) {
        return;
      }
      activeTourRef.current = null;
      cancelScheduledProgress();
      writeProgress({ op: "tourClear", id: current.id });
      enqueueOnboardingEvent({
        event: "tour_skipped",
        surface: current.source,
        itemId: current.id,
        step: current.stepIndex,
        meta: { reason: "branch_return" },
      });
      dispatch(endTour());
      dispatch(
        startTour({
          id: branchParentId,
          source: current.source,
          stepIndex: parentStep,
          route: window.location.pathname,
          inPlace: true,
        }),
      );
    });
  }, [
    activeTourId,
    branchParentId,
    branchAnchor,
    parentEntry,
    beforeChildAwait,
    dispatch,
    writeProgress,
  ]);

  const scope = tour?.scope ?? null;
  const inScopedStep = Boolean(scope && stepIndex > scope.entryStep);
  const branchReturnPending = Boolean(branchParentId) && beforeChildAwait;

  useEffect(() => {
    if (!activeTourId || !scope || !inScopedStep || branchReturnPending) {
      return undefined;
    }
    return observeAnchors(() => {
      const current = activeTourRef.current;
      if (!current || current.id !== activeTourId || queryVisibleAnchor(scope.anchor)) {
        return;
      }
      pauseTour("scope_left", scope.entryStep);
    });
  }, [activeTourId, scope, inScopedStep, branchReturnPending, pauseTour]);

  useEffect(() => {
    if (activeTour || pendingTour || suggestionsMuted) {
      return undefined;
    }
    const rejoinable = findRejoinableTours(remote, audience);
    if (rejoinable.length === 0) {
      return undefined;
    }
    return observeAnchors(() => {
      if (activeTourRef.current) {
        return;
      }
      const found = rejoinable.find(({ anchor }) => queryVisibleAnchor(anchor));
      if (!found) {
        return;
      }
      dispatch(
        startTour({
          id: found.id,
          source: "tour",
          stepIndex: found.stepIndex,
          route: window.location.pathname,
          rejoined: true,
          branchParentId: findSuspendedParent(remote, audience, found.id)?.id,
        }),
      );
    });
  }, [activeTour, pendingTour, remote, audience, suggestionsMuted, dispatch]);

  useEffect(() => {
    if (!targetEl || !step || !tour) {
      return undefined;
    }
    const isActionStep = step.advanceOn === "targetClick";
    if (step.hint && !isActionStep) {
      return undefined;
    }
    const isLastStep = stepIndex >= tour.steps.length - 1;
    const handleClick = () => {
      targetClickInFlight = true;
      window.setTimeout(() => {
        targetClickInFlight = false;
        if (isActionStep) {
          dispatch(pinTourPathname(null));
          next();
          return;
        }
        if (isLastStep) {
          completeTour();
          return;
        }
        pauseTour("target_click");
      }, 0);
    };
    if (step.hint) {
      const selector = `[data-tour="${CSS.escape(step.anchor)}"]`;
      const handleAnchorClick = (event: MouseEvent) => {
        if (event.target instanceof Element && event.target.closest(selector)) {
          handleClick();
        }
      };
      document.addEventListener("click", handleAnchorClick, { capture: true });
      return () => {
        document.removeEventListener("click", handleAnchorClick, { capture: true });
      };
    }
    targetEl.addEventListener("click", handleClick, { capture: true });
    return () => {
      targetEl.removeEventListener("click", handleClick, { capture: true });
    };
  }, [targetEl, step, tour, stepIndex, next, completeTour, pauseTour, dispatch]);

  const hasActiveTour = Boolean(activeTour);
  const escapeDismisses = hasActiveTour && !awaitStep;

  useEffect(() => {
    if (!escapeDismisses) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        dismiss();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [escapeDismisses, dismiss]);

  useEffect(() => {
    if (!dismissWatch || hasActiveTour || pendingTour || suggestionsMuted) {
      return undefined;
    }
    const watchedTour = getTour(dismissWatch.id, audience);
    if (!watchedTour) {
      return undefined;
    }
    const alreadyVisible = new Set(dismissWatch.visibleAtDismiss);
    const findReshowStep = (): number | null => {
      for (let index = dismissWatch.fromStep + 1; index < watchedTour.steps.length; index += 1) {
        const laterStep = watchedTour.steps[index];
        if (
          laterStep &&
          !isAwaitStep(laterStep) &&
          !alreadyVisible.has(laterStep.anchor) &&
          queryVisibleAnchor(laterStep.anchor)
        ) {
          return index;
        }
      }
      return null;
    };
    let frame: number | null = null;
    const check = () => {
      frame = null;
      const reshowStep = findReshowStep();
      if (reshowStep === null) {
        return;
      }
      observer.disconnect();
      dispatch(
        startTour({
          id: dismissWatch.id,
          source: dismissWatch.source,
          stepIndex: reshowStep,
          route: window.location.pathname,
          autoReshown: true,
        }),
      );
    };
    const observer = new MutationObserver(() => {
      if (frame === null) {
        frame = requestAnimationFrame(check);
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-tour", "style", "class", "hidden"],
    });
    check();
    return () => {
      observer.disconnect();
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
    };
  }, [dismissWatch, hasActiveTour, pendingTour, audience, suggestionsMuted, dispatch]);

  const focusCourseId = pendingTour?.focusCourseId;
  const focusLatestCourse = pendingTour?.focusLatestCourse === true;
  const focusedCourseRef = useRef<number | null>(null);
  const indexRequestedForRef = useRef<number | null>(null);

  useEffect(() => {
    if ((focusCourseId === undefined && !focusLatestCourse) || !tour?.hidden) {
      focusedCourseRef.current = null;
      return;
    }
    const requestKey = focusCourseId ?? -1;
    if (searchIndex.length === 0) {
      if (indexRequestedForRef.current !== requestKey) {
        indexRequestedForRef.current = requestKey;
        void dispatch(fetchSearchIndex({}));
      }
      return;
    }
    const course =
      focusCourseId !== undefined
        ? searchIndex.find(
            (item) => item.type === "projectGroup" && Number(item.id) === focusCourseId,
          )
        : [...searchIndex]
            .filter((item) => item.type === "projectGroup" && item.updatedAt)
            .sort((a, b) => Date.parse(b.updatedAt ?? "") - Date.parse(a.updatedAt ?? ""))[0];
    const resolvedId = course ? Number(course.id) : requestKey;
    if (focusedCourseRef.current === resolvedId) {
      return;
    }
    focusedCourseRef.current = resolvedId;
    if (course) {
      void dispatch(
        searchProjectGroups({
          query: course.name,
          componentId: PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
        }),
      );
    }
  }, [focusCourseId, focusLatestCourse, tour?.hidden, searchIndex, dispatch]);

  useEffect(() => {
    if (!pendingTour || hasActiveTour) {
      return undefined;
    }
    const {
      id: pendingTourId,
      source: pendingTourSource,
      stepsBefore,
      stepIndex: storedStepIndex,
      focusCourseId: pendingFocusCourseId,
      focusLatestCourse: pendingFocusLatestCourse,
      replay: pendingReplay,
    } = pendingTour;
    const currentTour = getTour(pendingTourId, audience);
    const firstStep = currentTour?.steps[0];
    if (!currentTour || !firstStep || isAwaitStep(firstStep)) {
      return undefined;
    }
    const storedStep =
      storedStepIndex !== undefined ? currentTour.steps[storedStepIndex] : undefined;
    const storedAnchor =
      storedStep && !isAwaitStep(storedStep) && storedStepIndex !== 0 ? storedStep.anchor : null;
    const controller = new AbortController();
    let settled = false;
    let graceTimer: ReturnType<typeof setTimeout> | null = null;
    const giveUp = (): void => {
      if (settled) {
        return;
      }
      settled = true;
      controller.abort();
      dispatch(setPendingTour(null));
      if (currentTour.locationHintKey) {
        dispatch(setLocationHintTour(pendingTourId));
      }
      enqueueOnboardingEvent({
        event: "tour_target_missing",
        surface: pendingTourSource,
        itemId: pendingTourId,
        step: 0,
      });
    };
    void waitForFirstAnchorSettled(
      storedAnchor ? [storedAnchor, firstStep.anchor] : [firstStep.anchor],
      PENDING_TOUR_TIMEOUT_MS,
      controller.signal,
    ).then(({ found }) => {
      if (controller.signal.aborted || settled) {
        return;
      }
      if (found === null) {
        giveUp();
        return;
      }
      settled = true;
      if (pendingFocusCourseId !== undefined || pendingFocusLatestCourse === true) {
        void dispatch(
          searchProjectGroups({
            query: "",
            componentId: PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
          }),
        );
      }
      dispatch(
        startTour({
          id: pendingTourId,
          source: pendingTourSource,
          stepIndex: found.anchorId === storedAnchor ? storedStepIndex : 0,
          positionOffset: { before: stepsBefore ?? 0, after: 0 },
          replay: Boolean(pendingReplay),
        }),
      );
    });
    if (currentTour.presenceAnchor) {
      void waitForAnchorSettled(
        currentTour.presenceAnchor,
        PENDING_TOUR_TIMEOUT_MS,
        controller.signal,
      ).then(({ found: presence }) => {
        if (!presence || controller.signal.aborted || settled) {
          return;
        }
        graceTimer = setTimeout(giveUp, STEP_ANCHOR_TIMEOUT_MS);
      });
    }

    return () => {
      controller.abort();
      if (graceTimer !== null) {
        clearTimeout(graceTimer);
      }
    };
  }, [pendingTour, hasActiveTour, dispatch, t, audience]);

  const whileOpen = step?.whileOpen ?? null;
  const [openTargetEl, setOpenTargetEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!whileOpen || !targetEl) {
      setOpenTargetEl(null);
      return undefined;
    }
    const check = () => {
      const found = queryVisibleAnchor(whileOpen.anchor);
      setOpenTargetEl((previous) => (previous === found ? previous : found));
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class", "hidden"],
    });
    return () => observer.disconnect();
  }, [whileOpen, targetEl]);

  const displayStep: AnchorTourStep | null =
    step && whileOpen && openTargetEl
      ? {
          ...step,
          anchor: whileOpen.anchor,
          titleKey: whileOpen.titleKey,
          bodyKey: whileOpen.bodyKey,
          placement: whileOpen.placement ?? step.placement,
          whileOpen: undefined,
        }
      : step;

  return {
    tour,
    step: displayStep,
    stepIndex,
    targetEl: openTargetEl ?? targetEl,
    prefersReducedMotion,
    next,
    back,
    skip,
    dismiss,
    handoff,
  };
};

export default useTourRunner;
